/* ═══════════════════════════════════════════════════════════════
   MOTOR UNIFICADO DE PROGRAMAS Y PROGRAMACIÓN
   Fuente canónica única para /admin/programs y /admin/schedule.

   Principios:
   - Un solo catálogo de programas (~14 programas)
   - Una sola parrilla de horarios (slots vinculados por programId)
   - Programs aporta la ficha (host, categoría, descripción, imagen)
   - Schedule aporta el slot (día, hora inicio, hora fin)
   - Hosts referencian personas canónicas de persons.ts por ID

   Resolución de contradicciones (Sección C de ADMIN-AUDIT-001):
   - "Despierta con Energía" + "El Mañanero" + "Mañanas con Energía" → "El Mañanero" (Valentina Ríos)
   - Andrea Castillo → persona canónica separada (no es Andrés Castillo)
   - Valentina Herrera → alias de Valentina Ríos
   - DJ Marco → persona canónica p-015
   - Memo López → persona canónica p-016
   ═══════════════════════════════════════════════════════════════ */

import { canonicalPersons } from './persons';

export type ProgramCategory = 'musical' | 'noticias' | 'deportes' | 'entretenimiento' | 'entrevistas' | 'cultural' | 'tecnologia' | 'variedades' | 'educacion' | 'especial' | 'evento';

export interface CanonicalProgram {
  id: string;
  title: string;
  slug: string;
  description: string;
  category: ProgramCategory;
  status: 'active' | 'inactive';
  coverImage: string;
  hostPersonIds: string[];
  producerPersonIds: string[];
  language: string;
  featured: boolean;
  followers: number;
  interactions: { likes: number; comments: number; shares: number };
  upcomingEpisodes: { id: number; title: string; description: string; date: string; guest?: string; isSpecial: boolean }[];
  createdAt: string;
  updatedAt: string;
}

export interface CanonicalScheduleSlot {
  id: string;
  programId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  timezone: string;
  isLive: boolean;
  isRecurring: boolean;
  isActive: boolean;
  cancelled?: boolean;
  specialEventName?: string;
  startDate?: string;
  type: 'musica' | 'programa_vivo' | 'noticias' | 'deportes' | 'entrevista' | 'especial' | 'evento';
  color?: string;
}

/* ═══════════════════════════════════════════════════════════════
   PROGRAMAS CANÓNICOS (14)
   ═══════════════════════════════════════════════════════════════ */

export const canonicalPrograms: CanonicalProgram[] = [
  {
    id: 'prog-001',
    title: 'El Mañanero',
    slug: 'el-mananero',
    description: 'El programa matutino que despierta a la ciudad. Música, noticias, entrevistas con invitados especiales y la mejor energía para arrancar el día. Valentina te acompaña cada mañana con los temas que importan, los hits del momento y las historias que todos comentan.',
    category: 'entretenimiento',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20studio%20setup%20with%20warm%20golden%20lighting%2C%20professional%20microphones%20on%20a%20wooden%20desk%2C%20coffee%20cups%20and%20notes%20scattered%20naturally%2C%20modern%20broadcast%20equipment%2C%20cozy%20vibrant%20morning%20atmosphere%2C%20rich%20amber%20and%20cream%20tones%2C%20editorial%20radio%20studio%20photography&width=1200&height=600&seq=program-mananero-v2&orientation=landscape',
    hostPersonIds: ['p-002'],
    producerPersonIds: ['p-001'],
    language: 'es',
    featured: true,
    followers: 48200,
    interactions: { likes: 187200, comments: 28400, shares: 18960 },
    upcomingEpisodes: [{ id: 101, title: 'Entrevista con Natalia Lafourcade', description: 'La cantautora mexicana nos visita para hablar de su nuevo álbum y su gira internacional.', date: '2026-06-23T08:00:00Z', guest: 'Natalia Lafourcade', isSpecial: true }],
    createdAt: '2024-01-20T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-002',
    title: 'La Hora Pico',
    slug: 'la-hora-pico',
    description: 'El pulso musical de la tarde. Andrés te trae los beats más frescos, los remixes que están sonando en todas las pistas y las colaboraciones más explosivas del momento. Dos horas de pura energía.',
    category: 'musical',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Evening%20radio%20DJ%20studio%20with%20colorful%20purple%20and%20orange%20neon%20lighting%2C%20DJ%20mixer%20and%20headphones%20on%20desk%2C%20modern%20urban%20atmosphere%2C%20energetic%20music%20broadcast%20setup%2C%20vibrant%20synthwave%20aesthetic%2C%20warm%20neon%20glow&width=1200&height=600&seq=program-pico-v2&orientation=landscape',
    hostPersonIds: ['p-004'],
    producerPersonIds: ['p-010'],
    language: 'es',
    featured: false,
    followers: 35100,
    interactions: { likes: 142500, comments: 21540, shares: 14280 },
    upcomingEpisodes: [],
    createdAt: '2024-04-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-003',
    title: 'Zona Deportiva',
    slug: 'zona-deportiva',
    description: 'La cita obligada para los fanáticos del deporte. Roberto Aguilar te trae análisis, estadísticas, entrevistas con atletas y la cobertura más completa de fútbol, béisbol, básquet y todo el deporte internacional.',
    category: 'deportes',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Sports%20radio%20studio%20with%20sports%20memorabilia%20on%20walls%2C%20jerseys%20framed%20on%20dark%20backdrop%2C%20multiple%20screens%20showing%20game%20highlights%2C%20energetic%20broadcast%20atmosphere%2C%20professional%20lighting%20with%20green%20and%20white%20accents%2C%20dynamic%20sports%20photography%20style&width=1200&height=600&seq=program-deportiva-v2&orientation=landscape',
    hostPersonIds: ['p-003'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 28900,
    interactions: { likes: 116300, comments: 18200, shares: 10700 },
    upcomingEpisodes: [],
    createdAt: '2024-07-20T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-004',
    title: 'Entrevistas Exclusivas',
    slug: 'entrevistas-exclusivas',
    description: 'Conversaciones profundas con las personalidades más influyentes de la música, el arte, la política y la cultura. Carlos Mendoza recibe a invitados nacionales e internacionales en un espacio íntimo donde las historias cobran vida.',
    category: 'entrevistas',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Intimate%20radio%20interview%20studio%20setup%20with%20two%20leather%20armchairs%20facing%20each%20other%2C%20warm%20amber%20ambient%20lighting%2C%20professional%20vintage%20microphones%2C%20sophisticated%20cream%20and%20charcoal%20tones%2C%20elegant%20broadcast%20atmosphere%2C%20editorial%20photography&width=1200&height=600&seq=program-entrevistas-v2&orientation=landscape',
    hostPersonIds: ['p-001'],
    producerPersonIds: ['p-010'],
    language: 'es',
    featured: true,
    followers: 22400,
    interactions: { likes: 75200, comments: 15600, shares: 7800 },
    upcomingEpisodes: [],
    createdAt: '2024-02-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-005',
    title: 'Beats & Bytes',
    slug: 'beats-and-bytes',
    description: 'Donde la música se encuentra con la tecnología. Andrés y Lucía exploran las últimas tendencias en producción musical, gear, software, inteligencia artificial aplicada al audio, y el futuro de la industria sonora.',
    category: 'tecnologia',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Futuristic%20radio%20studio%20with%20digital%20screens%20showing%20colorful%20audio%20waveforms%2C%20modern%20tech%20equipment%20on%20desk%2C%20neon%20cyan%20and%20magenta%20accents%2C%20sleek%20minimalist%20broadcast%20environment%2C%20cyberpunk-lite%20aesthetic%2C%20clean%20modern%20photography&width=1200&height=600&seq=program-beats-v2&orientation=landscape',
    hostPersonIds: ['p-004'],
    producerPersonIds: ['p-012'],
    language: 'es',
    featured: false,
    followers: 18700,
    interactions: { likes: 65900, comments: 11200, shares: 5300 },
    upcomingEpisodes: [],
    createdAt: '2024-06-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-006',
    title: 'Top 40 Countdown',
    slug: 'top-40-countdown',
    description: 'El ranking semanal de los 40 temas más escuchados en RadioFrecuenciaWeb. Valentina y Sofía te guían por el conteo con datos curiosos, historias detrás de cada canción y las novedades de la escena pop.',
    category: 'musical',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Vibrant%20pop%20music%20countdown%20show%20studio%2C%20colorful%20gradient%20LED%20panels%20with%20numbers%2C%20energetic%20atmosphere%2C%20two%20young%20radio%20hosts%20laughing%20with%20microphones%2C%20bright%20fun%20lighting%20with%20pink%20and%20coral%20tones%2C%20music%20chart%20celebration%20vibe&width=1200&height=600&seq=program-top40-v2&orientation=landscape',
    hostPersonIds: ['p-002'],
    producerPersonIds: ['p-006'],
    language: 'es',
    featured: false,
    followers: 52100,
    interactions: { likes: 214200, comments: 35600, shares: 18000 },
    upcomingEpisodes: [],
    createdAt: '2024-03-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-007',
    title: 'Sesiones Acústicas',
    slug: 'sesiones-acusticas',
    description: 'Un espacio íntimo dedicado a la música en su forma más pura. Lucía recibe a artistas emergentes y consolidados para sesiones acústicas en vivo, donde las canciones brillan sin artificios.',
    category: 'cultural',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20music%20session%20space%20with%20vintage%20guitar%20on%20stand%2C%20soft%20warm%20candlelight%20atmosphere%2C%20professional%20microphones%2C%20rich%20wooden%20textures%20and%20cream%20fabrics%2C%20cozy%20creative%20studio%20ambiance%2C%20warm%20sepia%20and%20amber%20tones%2C%20artistic%20photography&width=1200&height=600&seq=program-acusticas-v2&orientation=landscape',
    hostPersonIds: ['p-012'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 16300,
    interactions: { likes: 58400, comments: 8700, shares: 4100 },
    upcomingEpisodes: [],
    createdAt: '2024-08-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-008',
    title: 'Noticias al Instante',
    slug: 'noticias-al-instante',
    description: 'El resumen informativo que necesitás para estar al día. Cada mediodía, Carlos te trae las noticias más relevantes de Santa Rosa, California, el país y el mundo. Información verificada, análisis objetivo.',
    category: 'noticias',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=News%20radio%20studio%20with%20multiple%20screens%20showing%20headlines%20and%20maps%2C%20professional%20dark%20wood%20broadcast%20desk%2C%20clean%20modern%20journalism%20aesthetic%2C%20navy%20and%20white%20tones%20with%20warm%20accent%20lighting%2C%20serious%20professional%20atmosphere%2C%20editorial%20newsroom%20photography&width=1200&height=600&seq=program-noticias-v2&orientation=landscape',
    hostPersonIds: ['p-001'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 31200,
    interactions: { likes: 119800, comments: 24600, shares: 12000 },
    upcomingEpisodes: [],
    createdAt: '2024-01-25T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-009',
    title: 'Mañana Activa',
    slug: 'manana-activa',
    description: 'Valentina toma el control de la media mañana con los últimos estrenos del pop, entrevistas exclusivas con artistas y los segmentos favoritos de la audiencia.',
    category: 'musical',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Bright%20morning%20radio%20show%20with%20pop%20music%20theme%2C%20warm%20natural%20studio%20lighting%2C%20modern%20minimalist%20broadcast%20desk%2C%20fresh%20energetic%20atmosphere%20with%20green%20plants%2C%20clean%20professional%20setup&width=800&height=450&seq=program-manana-activa&orientation=landscape',
    hostPersonIds: ['p-002'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 21400,
    interactions: { likes: 78200, comments: 15200, shares: 9800 },
    upcomingEpisodes: [],
    createdAt: '2024-03-15T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-010',
    title: 'Tardes de Pop',
    slug: 'tardes-de-pop',
    description: 'Carlos Mendoza te trae la mejor selección de pop en español e inglés cada tarde. Concursos, llamadas en vivo y la sección "Flashback" donde recordamos los clásicos que marcaron época.',
    category: 'musical',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Vibrant%20afternoon%20radio%20studio%20with%20pop%20music%20theme%2C%20colorful%20warm%20ambient%20lighting%20with%20orange%20and%20pink%20tones%2C%20professional%20DJ%20setup%20with%20headphones%2C%20modern%20cozy%20broadcast%20space&width=800&height=450&seq=sched-img-tardes-pop&orientation=landscape',
    hostPersonIds: ['p-001'],
    producerPersonIds: [],
    language: 'es',
    featured: true,
    followers: 41200,
    interactions: { likes: 152300, comments: 32100, shares: 21500 },
    upcomingEpisodes: [],
    createdAt: '2024-02-10T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-011',
    title: 'Noche de Estrenos',
    slug: 'noche-de-estrenos',
    description: 'Las primeras escuchas de los álbumes más esperados. Valentina presenta en exclusiva los estrenos musicales de la semana, con análisis pista por pista y la opinión de los oyentes en vivo.',
    category: 'musical',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Nighttime%20radio%20studio%20with%20moody%20atmospheric%20lighting%2C%20professional%20microphones%2C%20vinyl%20records%20visible%2C%20warm%20amber%20and%20deep%20purple%20tones%2C%20sophisticated%20music%20review%20show%20aesthetic&width=800&height=450&seq=program-noche-estrenos&orientation=landscape',
    hostPersonIds: ['p-002'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 18200,
    interactions: { likes: 63200, comments: 12400, shares: 8900 },
    upcomingEpisodes: [],
    createdAt: '2024-05-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-012',
    title: 'Viernes de Fiesta',
    slug: 'viernes-de-fiesta',
    description: '¡Arranca el finde con toda la actitud! Carlos trae la playlist más prendida, invitados sorpresa, sorteos en vivo y la mejor energía para empezar el fin de semana.',
    category: 'entretenimiento',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Friday%20party%20radio%20show%20with%20festive%20colorful%20atmosphere%2C%20DJ%20setup%20and%20confetti%20visuals%2C%20energetic%20warm%20lighting%20with%20gold%20and%20coral%20tones%2C%20celebration%20broadcast%20vibe&width=800&height=450&seq=program-viernes-fiesta&orientation=landscape',
    hostPersonIds: ['p-001'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 35600,
    interactions: { likes: 125400, comments: 28400, shares: 19800 },
    upcomingEpisodes: [],
    createdAt: '2024-04-20T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-013',
    title: 'Miércoles de Conciertos',
    slug: 'miercoles-de-conciertos',
    description: 'DJ Marco revive los mejores conciertos de la historia cada miércoles por la noche. Sets en vivo, mezclas exclusivas y la energía de estar en primera fila.',
    category: 'especial',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Concert%20themed%20radio%20show%20studio%20with%20stage%20lighting%20effects%2C%20rock%20concert%20atmosphere%2C%20dramatic%20warm%20and%20purple%20lighting%2C%20live%20music%20broadcast%20energy%2C%20professional%20audio%20equipment&width=800&height=450&seq=program-miercoles-conciertos&orientation=landscape',
    hostPersonIds: ['p-015'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 24500,
    interactions: { likes: 89200, comments: 19800, shares: 14500 },
    upcomingEpisodes: [],
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
  {
    id: 'prog-014',
    title: 'Domingo Familiar',
    slug: 'domingo-familiar',
    description: 'El plan perfecto para el domingo en familia. Música tranquila, segmentos para niños, recetas y los mejores consejos para empezar la semana con buena energía.',
    category: 'variedades',
    status: 'active',
    coverImage: 'https://readdy.ai/api/search-image?query=Warm%20family-friendly%20Sunday%20radio%20show%2C%20cozy%20home-inspired%20studio%20with%20soft%20natural%20lighting%2C%20plants%20and%20books%20visible%2C%20relaxed%20peaceful%20broadcast%20atmosphere%20with%20cream%20and%20sage%20tones&width=800&height=450&seq=program-domingo-familiar&orientation=landscape',
    hostPersonIds: ['p-014'],
    producerPersonIds: [],
    language: 'es',
    featured: false,
    followers: 19500,
    interactions: { likes: 67800, comments: 14200, shares: 10200 },
    upcomingEpisodes: [],
    createdAt: '2024-07-01T10:00:00Z',
    updatedAt: '2026-06-19T10:00:00Z',
  },
];

/* ═══════════════════════════════════════════════════════════════
   SLOTS DE PROGRAMACIÓN (Parrilla unificada, ~45 slots)
   ═══════════════════════════════════════════════════════════════ */

export const canonicalSlots: CanonicalScheduleSlot[] = [
  /* ── El Mañanero: L-V 06:00-10:00 ── */
  { id: 's-001', programId: 'prog-001', dayOfWeek: 1, startTime: '06:00', endTime: '10:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-002', programId: 'prog-001', dayOfWeek: 2, startTime: '06:00', endTime: '10:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-003', programId: 'prog-001', dayOfWeek: 3, startTime: '06:00', endTime: '10:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-004', programId: 'prog-001', dayOfWeek: 4, startTime: '06:00', endTime: '10:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-005', programId: 'prog-001', dayOfWeek: 5, startTime: '06:00', endTime: '10:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },

  /* ── Mañana Activa: L-V 10:00-12:00 ── */
  { id: 's-006', programId: 'prog-009', dayOfWeek: 1, startTime: '10:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-007', programId: 'prog-009', dayOfWeek: 2, startTime: '10:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-008', programId: 'prog-009', dayOfWeek: 3, startTime: '10:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-009', programId: 'prog-009', dayOfWeek: 4, startTime: '10:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-010', programId: 'prog-009', dayOfWeek: 5, startTime: '10:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },

  /* ── Entrevistas Exclusivas: Ma-Ju 11:00-12:30 ── */
  { id: 's-027', programId: 'prog-004', dayOfWeek: 2, startTime: '11:00', endTime: '12:30', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'entrevista' },
  { id: 's-028', programId: 'prog-004', dayOfWeek: 4, startTime: '11:00', endTime: '12:30', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'entrevista' },

  /* ── Noticias al Instante: L-V 12:30-13:15 ── */
  { id: 's-011', programId: 'prog-008', dayOfWeek: 1, startTime: '12:30', endTime: '13:15', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'noticias' },
  { id: 's-012', programId: 'prog-008', dayOfWeek: 2, startTime: '12:30', endTime: '13:15', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'noticias' },
  { id: 's-013', programId: 'prog-008', dayOfWeek: 3, startTime: '12:30', endTime: '13:15', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'noticias' },
  { id: 's-014', programId: 'prog-008', dayOfWeek: 4, startTime: '12:30', endTime: '13:15', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'noticias' },
  { id: 's-015', programId: 'prog-008', dayOfWeek: 5, startTime: '12:30', endTime: '13:15', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'noticias' },

  /* ── Tardes de Pop: L-J 14:00-17:00 ── */
  { id: 's-016', programId: 'prog-010', dayOfWeek: 1, startTime: '14:00', endTime: '17:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-017', programId: 'prog-010', dayOfWeek: 2, startTime: '14:00', endTime: '17:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-018', programId: 'prog-010', dayOfWeek: 3, startTime: '14:00', endTime: '17:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-019', programId: 'prog-010', dayOfWeek: 4, startTime: '14:00', endTime: '17:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },

  /* ── Viernes de Fiesta: V 14:00-20:00 ── */
  { id: 's-020', programId: 'prog-012', dayOfWeek: 5, startTime: '14:00', endTime: '20:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },

  /* ── La Hora Pico: L-V 17:00-19:00 ── */
  { id: 's-021', programId: 'prog-002', dayOfWeek: 1, startTime: '17:00', endTime: '19:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-022', programId: 'prog-002', dayOfWeek: 2, startTime: '17:00', endTime: '19:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-023', programId: 'prog-002', dayOfWeek: 3, startTime: '17:00', endTime: '19:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-024', programId: 'prog-002', dayOfWeek: 4, startTime: '17:00', endTime: '19:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-025', programId: 'prog-002', dayOfWeek: 5, startTime: '17:00', endTime: '19:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'programa_vivo' },

  /* ── Noche de Estrenos: L-J 19:00-23:00 ── */
  { id: 's-026', programId: 'prog-011', dayOfWeek: 1, startTime: '19:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-026b', programId: 'prog-011', dayOfWeek: 2, startTime: '19:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-026c', programId: 'prog-011', dayOfWeek: 3, startTime: '19:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-026d', programId: 'prog-011', dayOfWeek: 4, startTime: '19:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },

  /* ── Zona Deportiva: L-Mi-V 20:00-22:00 ── */
  { id: 's-029', programId: 'prog-003', dayOfWeek: 1, startTime: '20:00', endTime: '22:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'deportes' },
  { id: 's-030', programId: 'prog-003', dayOfWeek: 3, startTime: '20:00', endTime: '22:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'deportes' },
  { id: 's-031', programId: 'prog-003', dayOfWeek: 5, startTime: '20:00', endTime: '22:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'deportes' },

  /* ── Miércoles de Conciertos: Mi 20:00-23:00 (solapa con Zona Deportiva Mi 20-22) ── */
  { id: 's-032', programId: 'prog-013', dayOfWeek: 3, startTime: '20:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'especial' },

  /* ── Sábado ── */
  { id: 's-033', programId: '', dayOfWeek: 6, startTime: '08:00', endTime: '12:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica', specialEventName: 'Sábado Retro' },
  { id: 's-034', programId: 'prog-006', dayOfWeek: 6, startTime: '15:00', endTime: '17:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica' },
  { id: 's-035', programId: '', dayOfWeek: 6, startTime: '18:00', endTime: '21:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'musica', specialEventName: 'Urban Beats' },
  { id: 's-036', programId: '', dayOfWeek: 6, startTime: '20:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: true, isActive: true, type: 'musica', specialEventName: 'Noche de Electrónica' },
  { id: 's-037', programId: 'prog-005', dayOfWeek: 6, startTime: '21:00', endTime: '22:30', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'especial' },

  /* ── Domingo ── */
  { id: 's-038', programId: 'prog-014', dayOfWeek: 0, startTime: '10:00', endTime: '14:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'programa_vivo' },
  { id: 's-039', programId: 'prog-007', dayOfWeek: 0, startTime: '19:00', endTime: '20:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'especial' },
  { id: 's-040', programId: '', dayOfWeek: 0, startTime: '18:00', endTime: '21:00', timezone: 'America/Costa_Rica', isLive: false, isRecurring: true, isActive: true, type: 'especial', specialEventName: 'Domingo de Reflexión' },

  /* ── Eventos especiales ── */
  { id: 's-100', programId: '', dayOfWeek: 6, startTime: '14:00', endTime: '23:00', timezone: 'America/Costa_Rica', isLive: true, isRecurring: false, isActive: true, type: 'evento', specialEventName: 'Summer Fest 2026', startDate: '2026-06-14' },
  { id: 's-101', programId: '', dayOfWeek: 2, startTime: '20:00', endTime: '21:30', timezone: 'America/Costa_Rica', isLive: true, isRecurring: false, isActive: true, type: 'entrevista', specialEventName: 'Entrevista: Los Ecos', startDate: '2026-06-16' },
];

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */

export function getProgramById(id: string): CanonicalProgram | undefined {
  return canonicalPrograms.find((p) => p.id === id);
}

export function getProgramBySlug(slug: string): CanonicalProgram | undefined {
  return canonicalPrograms.find((p) => p.slug === slug);
}

export function getSlotsForProgram(programId: string): CanonicalScheduleSlot[] {
  return canonicalSlots.filter((s) => s.programId === programId && s.isActive);
}

export function getSlotsByDay(dayOfWeek: number): CanonicalScheduleSlot[] {
  return canonicalSlots.filter((s) => s.dayOfWeek === dayOfWeek && s.isActive).sort((a, b) => a.startTime.localeCompare(b.startTime));
}

export function getNowPlaying(): { now: CanonicalScheduleSlot | null; next: CanonicalScheduleSlot | null; program: CanonicalProgram | null } {
  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
  const todaySlots = getSlotsByDay(currentDay);
  let nowSlot: CanonicalScheduleSlot | null = null;
  let nextSlot: CanonicalScheduleSlot | null = null;
  for (let i = 0; i < todaySlots.length; i++) {
    if (todaySlots[i].startTime <= currentTime && todaySlots[i].endTime > currentTime) {
      nowSlot = todaySlots[i];
      nextSlot = i + 1 < todaySlots.length ? todaySlots[i + 1] : null;
      break;
    }
  }
  if (!nowSlot && todaySlots.length > 0) {
    const upcoming = todaySlots.filter((s) => s.startTime > currentTime);
    if (upcoming.length > 0) nextSlot = upcoming[0];
  }
  const program = nowSlot ? getProgramById(nowSlot.programId) || null : null;
  return { now: nowSlot, next: nextSlot, program };
}